import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import {
    localDateString, localTimeString, singleDateEntry, generateRepeatDates, defaultTitle,
} from '../../lib/conventionWizardDates';

/*
 * "Simple create" for conventions: pick a type, fill title + dates, done.
 * The settings per type come from GET /api/conventions/wizard/presets
 * (backend/lib/convention-presets.js) — this component never hard-codes
 * toggles, it only shows what the preset will set. The old 30-field form is
 * still one click away ("Advanced form").
 */

const PRESET_ORDER = ['training', 'dugnad', 'norgesfelt', 'competition'];
// Paid module: the IPSC preset is listed only for clubs with featureFlags.dynamicShooting
// (the backend refuses it otherwise — backend/lib/ipsc-module.js).
const IPSC_PRESET = 'ipsc';

const PRESET_META = {
    training: {
        icon: 'fa-duotone fa-qrcode',
        title: <Text id='convention.wizard-training'>Training</Text>,
        description: <Text id='convention.wizard-training-desc'>Members register themselves at the range with the QR code or the 6-digit code shown to the admin. No lag are created up front.</Text>,
    },
    dugnad: {
        icon: 'fa-duotone fa-people-carry-box',
        title: <Text id='convention.wizard-dugnad'>Dugnad</Text>,
        description: <Text id='convention.wizard-dugnad-desc'>Members register themselves at the venue with the QR code or the 6-digit code shown to the admin. Attendance counts in the yearly dugnad overview under Klubbdrift.</Text>,
    },
    norgesfelt: {
        icon: 'fa-duotone fa-bullseye-arrow',
        title: <Text id='convention.wizard-norgesfelt'>Norgesfelt</Text>,
        description: <Text id='convention.wizard-norgesfelt-desc'>Competition with open signup. An admin places each shooter on a lag. Norgesfelt lag are created every 45 minutes between start and end.</Text>,
    },
    competition: {
        icon: 'fa-duotone fa-trophy',
        title: <Text id='convention.wizard-competition'>Competition</Text>,
        description: <Text id='convention.wizard-competition-desc'>Full felt stevne: only officials enter scores, participant and lag lists are hidden, and an admin places each shooter on a lag. Felt lag every 10 minutes.</Text>,
    },
    ipsc: {
        icon: 'fa-duotone fa-person-rifle',
        title: <Text id='convention.wizard-ipsc'>IPSC (dynamisk)</Text>,
        description: <Text id='convention.wizard-ipsc-desc'>Dynamic shooting match: officials score each stage on their phone, shooters approve on the spot, results by hit factor per division. Squads every 15 minutes; an admin places each shooter.</Text>,
    },
};

const WEEKDAYS = [
    { key: 1, label: <Text id='convention.weekday-short-mon'>Mon</Text> },
    { key: 2, label: <Text id='convention.weekday-short-tue'>Tue</Text> },
    { key: 3, label: <Text id='convention.weekday-short-wed'>Wed</Text> },
    { key: 4, label: <Text id='convention.weekday-short-thu'>Thu</Text> },
    { key: 5, label: <Text id='convention.weekday-short-fri'>Fri</Text> },
    { key: 6, label: <Text id='convention.weekday-short-sat'>Sat</Text> },
    { key: 0, label: <Text id='convention.weekday-short-sun'>Sun</Text> },
];

const DEFAULT_START_TIME = '19:00';
const DEFAULT_END_TIME = '21:00';

@withText({
    titleTraining: <Text id='convention.wizard-title-training'>Training</Text>,
    titleDugnad: <Text id='convention.wizard-title-dugnad'>Dugnad</Text>,
    titleNorgesfelt: <Text id='convention.wizard-title-norgesfelt'>Norgesfelt</Text>,
    titleCompetition: <Text id='convention.wizard-title-competition'>Competition</Text>,
    titleIpsc: <Text id='convention.wizard-title-ipsc'>IPSC match</Text>,
    errorNoDates: <Text id='convention.wizard-error-no-dates'>Add at least one date first.</Text>,
    errorNoTitle: <Text id='convention.wizard-error-no-title'>Give the convention a title.</Text>,
    errorProduct: <Text id='convention.wizard-error-product'>Give the product a name and a price.</Text>,
    productFailed: <Text id='convention.wizard-product-failed'>Could not create the product</Text>,
    failedToast: <Text id='convention.wizard-failed'>Could not create conventions</Text>,
})
@observer
class ConventionWizard extends Component {
    constructor(props) {
        super(props);
        const today = new Date();
        const inThreeMonths = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
        this.state = {
            presets: [],
            preset: null,
            title: '',
            titleTouched: false,
            location: '',
            signupProduct: '',
            products: [],
            showNewProduct: false,
            newProductName: '',
            newProductPrice: '',
            newProductSaving: false,
            mode: 'single',
            date: localDateString(today),
            startTime: DEFAULT_START_TIME,
            endTime: DEFAULT_END_TIME,
            weekdays: [],
            fromDate: localDateString(today),
            toDate: localDateString(inThreeMonths),
            repeatDates: [],
            loading: false,
            error: null,
        };
    }

    componentDidMount() {
        this.loadPresets();
        this.loadProducts();
    }

    loadPresets = async () => {
        const { conventionStore } = this.props.stores;
        const presets = await conventionStore.loadWizardPresets();
        this.setState({ presets });
    }

    /**
     * The club everything in this wizard belongs to: the HOSTNAME club
     * (appState.currentCustomer, loaded from /api/customers/public), not the
     * caller's switched user.customer — a multi-club admin on
     * hurum.pistolklubb.app must get Hurum, never Dokka.
     */
    clubId = () => {
        const { appState } = this.props.stores;
        return appState.currentCustomer?.id > 0 ? appState.currentCustomer.id : undefined;
    }

    loadProducts = async () => {
        const { conventionStore } = this.props.stores;
        const products = await conventionStore.loadWizardProducts({ customer: this.clubId() });
        this.setState({ products });
    }

    toggleNewProduct = () => {
        const { showNewProduct } = this.state;
        this.setState({ showNewProduct: !showNewProduct, error: null });
    }

    createProduct = async () => {
        const { errorProduct, productFailed } = this.props;
        const { conventionStore } = this.props.stores;
        const {
            newProductName, newProductPrice, newProductSaving, products,
        } = this.state;
        if (newProductSaving) {
            return;
        }
        const name = newProductName.trim();
        const priceIncVat = parseFloat(String(newProductPrice).replace(',', '.'));
        if (!name || !(priceIncVat > 0)) {
            this.setState({ error: errorProduct });
            return;
        }
        this.setState({ newProductSaving: true, error: null });
        const response = await conventionStore.wizardCreateProduct({
            customer: this.clubId(),
            name,
            priceIncVat,
        });
        const product = response?.data?.product;
        if (response?.status === 201 && product?.id > 0) {
            this.setState({
                products: [product, ...products.filter(p => p.id !== product.id)],
                signupProduct: String(product.id),
                showNewProduct: false,
                newProductName: '',
                newProductPrice: '',
                newProductSaving: false,
            });
            return;
        }
        this.setState({ newProductSaving: false, error: response?.message || productFailed });
    }

    presetTitleLabel = (key) => {
        const {
            titleTraining, titleDugnad, titleNorgesfelt, titleCompetition, titleIpsc,
        } = this.props;
        if (key === IPSC_PRESET) {
            return titleIpsc;
        }
        if (key === 'dugnad') {
            return titleDugnad;
        }
        if (key === 'norgesfelt') {
            return titleNorgesfelt;
        }
        if (key === 'competition') {
            return titleCompetition;
        }
        return titleTraining;
    }

    choosePreset = (key) => {
        const { appState } = this.props.stores;
        const { titleTouched } = this.state;
        const customerName = appState.currentCustomer?.name;
        const next = { preset: key, error: null };
        if (!titleTouched) {
            next.title = defaultTitle(customerName, this.presetTitleLabel(key));
        }
        this.setState(next);
    }

    handleTitle = (e) => {
        this.setState({ title: e.target.value, titleTouched: true, error: null });
    }

    handleField = (field) => (e) => {
        this.setState({ [field]: e.target.value, error: null });
    }

    setMode = (mode) => {
        this.setState({ mode, error: null });
    }

    toggleWeekday = (key) => {
        const { weekdays } = this.state;
        const next = weekdays.includes(key) ? weekdays.filter(d => d !== key) : [...weekdays, key];
        this.setState({ weekdays: next.sort((a, b) => a - b), repeatDates: [], error: null });
    }

    generateDates = () => {
        const {
            weekdays, fromDate, toDate, startTime, endTime,
        } = this.state;
        const repeatDates = generateRepeatDates({
            weekdays, fromDate, toDate, startTime, endTime,
        });
        this.setState({ repeatDates, error: repeatDates.length === 0 ? this.props.errorNoDates : null });
    }

    removeDate = (index) => {
        const { repeatDates } = this.state;
        this.setState({ repeatDates: repeatDates.filter((_, i) => i !== index) });
    }

    openAdvanced = () => {
        const { drawerLevel = 1, callback } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('newConvention', {
            height: drawerHeightLarge,
            callback,
        }, drawerLevel + 1);
    }

    datesToCreate = () => {
        const {
            mode, date, startTime, endTime, repeatDates,
        } = this.state;
        if (mode === 'repeat') {
            return repeatDates;
        }
        const entry = singleDateEntry({ date, startTime, endTime });
        return entry ? [entry] : [];
    }

    create = async () => {
        const {
            callback = () => {}, errorNoDates, errorNoTitle, failedToast,
        } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const {
            preset, title, location, signupProduct, loading,
        } = this.state;
        if (loading || !preset) {
            return;
        }
        if (!title.trim()) {
            this.setState({ error: errorNoTitle });
            return;
        }
        const dates = this.datesToCreate();
        if (dates.length === 0) {
            this.setState({ error: errorNoDates });
            return;
        }
        this.setState({ loading: true, error: null });
        const response = await conventionStore.wizardCreate({
            preset,
            title: title.trim(),
            location: location.trim(),
            signupProduct: signupProduct ? parseInt(signupProduct, 10) : 0,
            dates,
            customer: this.clubId(),
        });
        if (response?.status === 201) {
            const count = Array.isArray(response.data) ? response.data.length : dates.length;
            appState.addToast({
                type: 'success',
                message: <Text id='convention.wizard-created' fields={{ count }}>Created {count} conventions</Text>,
            }, 5000);
            this.setState({ loading: false });
            callback();
            return;
        }
        this.setState({ loading: false, error: response?.message || failedToast });
    }

    renderPresetSummary(presetDef) {
        if (!presetDef) {
            return null;
        }
        const { fields, stageType } = presetDef;
        const items = [];
        if (fields.isCompetition) {
            items.push(<Text id='convention.wizard-sets-competition'>Competition</Text>);
        } else {
            items.push(<Text id='convention.wizard-sets-training'>Training (not a competition)</Text>);
        }
        if (fields.selfSignup && !fields.registrationIsOpen) {
            items.push(<Text id='convention.wizard-sets-qr'>Self-registration with QR / code at the range</Text>);
        }
        if (fields.registrationIsOpen) {
            items.push(<Text id='convention.wizard-sets-open-signup'>Open signup in the app</Text>);
        }
        if (fields.isApproved) {
            items.push(<Text id='convention.wizard-sets-admin-lag'>Admin places shooters on lag</Text>);
        }
        if (fields.isAdminScoring) {
            items.push(<Text id='convention.wizard-sets-admin-scoring'>Only officials enter scores</Text>);
        }
        if (fields.hideParticipants || fields.hideStages) {
            items.push(<Text id='convention.wizard-sets-hidden-lists'>Participant and lag lists hidden</Text>);
        }
        if (fields.public) {
            items.push(<Text id='convention.wizard-sets-public'>Visible to everyone</Text>);
        }
        return (
            <div class='d-flex flex-wrap mt-2'>
                {items.map((item, idx) => (
                    <span key={`preset-item-${idx}`} class='badge text-bg-secondary me-1 mb-1 fw-normal'>
                        <i class='fa-solid fa-check me-1' />{item}
                    </span>
                ))}
                {stageType ? (
                    <span class='badge text-bg-secondary me-1 mb-1 fw-normal'>
                        <i class='fa-solid fa-check me-1' />
                        <Text
                            id='convention.wizard-sets-lag'
                            fields={{ type: stageType, interval: fields.stageInterval, slots: fields.stageSlots }}
                        >
                            {stageType} lag every {fields.stageInterval} min, {fields.stageSlots} slots
                        </Text>
                    </span>
                ) : (
                    <span class='badge text-bg-secondary me-1 mb-1 fw-normal'>
                        <i class='fa-solid fa-check me-1' />
                        <Text id='convention.wizard-sets-no-lag'>No lag up front</Text>
                    </span>
                )}
            </div>
        );
    }

    render() {
        const {
            presets, preset, title, location, signupProduct, products, mode,
            date, startTime, endTime, weekdays, fromDate, toDate, repeatDates, loading, error,
            showNewProduct, newProductName, newProductPrice, newProductSaving,
        } = this.state;
        const presetDef = presets.find(p => p.key === preset);
        const dates = this.datesToCreate();
        const count = dates.length;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 110px;'>
                    <div class='d-flex justify-content-between align-items-center mx-3 mb-3'>
                        <h5 class='mb-0'><Text id='convention.wizard-heading'>New convention / training</Text></h5>
                        <button type='button' class='btn btn-sm btn-link text-body-secondary' onClick={this.openAdvanced}>
                            <i class='fa-duotone fa-sliders me-1' />
                            <Text id='convention.wizard-advanced'>Advanced form</Text>
                        </button>
                    </div>

                    {/* 1. Type */}
                    <div class='mx-3 mb-3'>
                        <div class='text-body-secondary small text-uppercase mb-2'>
                            1. <Text id='convention.wizard-step-type'>What are you creating?</Text>
                        </div>
                        <div class='row g-2'>
                            {(this.props.stores.appState.currentCustomer?.featureFlags?.dynamicShooting > 0
                                ? [...PRESET_ORDER, IPSC_PRESET] : PRESET_ORDER).map(key => {
                                const meta = PRESET_META[key];
                                const active = preset === key;
                                return (
                                    <div key={key} class='col-12 col-md-4'>
                                        <button
                                            type='button'
                                            class={`card w-100 h-100 text-start p-3 ${active ? 'border-primary bg-primary-subtle' : ''}`}
                                            onClick={() => this.choosePreset(key)}
                                        >
                                            <div class='d-flex align-items-center mb-1'>
                                                <i class={`${meta.icon} me-2 fs-4 ${active ? 'text-primary' : 'text-body-secondary'}`} />
                                                <strong>{meta.title}</strong>
                                            </div>
                                            <small class='text-body-secondary'>{meta.description}</small>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        {this.renderPresetSummary(presetDef)}
                    </div>

                    {preset && (<>
                        {/* 2. Details */}
                        <div class='mx-3 mb-3'>
                            <div class='text-body-secondary small text-uppercase mb-2'>
                                2. <Text id='convention.wizard-step-details'>Details</Text>
                            </div>
                            <div class='card p-3'>
                                <div class='mb-3'>
                                    <label class='form-label small text-body-secondary'><Text id='convention.wizard-title'>Title</Text></label>
                                    <input class='form-control' type='text' value={title} onInput={this.handleTitle} />
                                </div>
                                <div class='row g-3'>
                                    <div class='col-12 col-md-6'>
                                        <label class='form-label small text-body-secondary'><Text id='convention.wizard-location'>Location</Text></label>
                                        <input class='form-control' type='text' value={location} onInput={this.handleField('location')} />
                                    </div>
                                    <div class='col-12 col-md-6'>
                                        <label class='form-label small text-body-secondary'><Text id='convention.wizard-signup-product'>Signup fee (product)</Text></label>
                                        <div class='input-group'>
                                            <select class='form-select' value={signupProduct} onChange={this.handleField('signupProduct')}>
                                                <option value=''>— <Text id='convention.wizard-no-signup-product'>No signup fee</Text> —</option>
                                                {products.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}{p.priceIncVat > 0 ? ` (${p.priceIncVat} kr)` : ''}</option>
                                                ))}
                                            </select>
                                            <button
                                                type='button'
                                                class={`btn ${showNewProduct ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                onClick={this.toggleNewProduct}
                                                title='Nytt produkt'
                                            >
                                                <i class='fa-duotone fa-plus me-1' />
                                                <Text id='convention.wizard-new-product'>New</Text>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {showNewProduct && (
                                    <div class='border rounded p-3 mt-3 bg-body-secondary'>
                                        <div class='small text-body-secondary mb-2'>
                                            <Text id='convention.wizard-new-product-help'>Creates a product on this club with 25 % VAT. Enter the price the shooter pays.</Text>
                                        </div>
                                        <div class='row g-2 align-items-end'>
                                            <div class='col-12 col-md-6'>
                                                <label class='form-label small text-body-secondary'><Text id='convention.wizard-new-product-name'>Product name</Text></label>
                                                <input class='form-control' type='text' value={newProductName} onInput={this.handleField('newProductName')} placeholder='Treningsavgift' />
                                            </div>
                                            <div class='col-6 col-md-3'>
                                                <label class='form-label small text-body-secondary'><Text id='convention.wizard-new-product-price'>Price inc VAT (kr)</Text></label>
                                                <input class='form-control' type='number' min='1' step='1' value={newProductPrice} onInput={this.handleField('newProductPrice')} />
                                            </div>
                                            <div class='col-6 col-md-3'>
                                                <button type='button' class='btn btn-success w-100' onClick={this.createProduct} disabled={newProductSaving}>
                                                    {newProductSaving ? <i class='fa-duotone fa-spinner-third fa-spin me-1' /> : <i class='fa-duotone fa-check me-1' />}
                                                    <Text id='convention.wizard-new-product-create'>Create product</Text>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 3. Dates */}
                        <div class='mx-3 mb-3'>
                            <div class='text-body-secondary small text-uppercase mb-2'>
                                3. <Text id='convention.wizard-step-dates'>When?</Text>
                            </div>
                            <div class='card p-3'>
                                <div class='btn-group mb-3' role='group'>
                                    <button type='button' class={`btn btn-sm ${mode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setMode('single')}>
                                        <Text id='convention.wizard-mode-single'>One date</Text>
                                    </button>
                                    <button type='button' class={`btn btn-sm ${mode === 'repeat' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setMode('repeat')}>
                                        <Text id='convention.wizard-mode-repeat'>Repeat weekly</Text>
                                    </button>
                                </div>

                                {mode === 'repeat' && (
                                    <div class='d-flex flex-wrap mb-3'>
                                        {WEEKDAYS.map(day => (
                                            <button
                                                key={day.key}
                                                type='button'
                                                class={`btn btn-sm me-1 mb-1 rounded-pill ${weekdays.includes(day.key) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => this.toggleWeekday(day.key)}
                                            >
                                                {day.label}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div class='row g-3'>
                                    {mode === 'single' ? (
                                        <div class='col-6 col-md-4'>
                                            <label class='form-label small text-body-secondary'><Text id='convention.wizard-date'>Date</Text></label>
                                            <input class='form-control' type='date' value={date} onInput={this.handleField('date')} />
                                        </div>
                                    ) : (<>
                                        <div class='col-6 col-md-3'>
                                            <label class='form-label small text-body-secondary'><Text id='convention.from-date'>From date</Text></label>
                                            <input class='form-control' type='date' value={fromDate} onInput={this.handleField('fromDate')} />
                                        </div>
                                        <div class='col-6 col-md-3'>
                                            <label class='form-label small text-body-secondary'><Text id='convention.to-date'>To date</Text></label>
                                            <input class='form-control' type='date' value={toDate} onInput={this.handleField('toDate')} />
                                        </div>
                                    </>)}
                                    <div class='col-6 col-md-3'>
                                        <label class='form-label small text-body-secondary'><Text id='convention.start-time'>Start time</Text></label>
                                        <input class='form-control' type='time' value={startTime} onInput={this.handleField('startTime')} />
                                    </div>
                                    <div class='col-6 col-md-3'>
                                        <label class='form-label small text-body-secondary'><Text id='convention.end-time'>End time</Text></label>
                                        <input class='form-control' type='time' value={endTime} onInput={this.handleField('endTime')} />
                                    </div>
                                </div>

                                {mode === 'repeat' && (<>
                                    <button
                                        type='button'
                                        class='btn btn-outline-success rounded-pill mt-3 align-self-start'
                                        onClick={this.generateDates}
                                        disabled={weekdays.length === 0}
                                    >
                                        <i class='fa-duotone fa-calendar-plus me-2' />
                                        <Text id='convention.generate-dates'>Generate dates from pattern</Text>
                                    </button>
                                    {repeatDates.length > 0 && (
                                        <ul class='list-group mt-3'>
                                            {repeatDates.map((d, index) => (
                                                <li key={d.startDate} class='list-group-item d-flex justify-content-between align-items-center py-1'>
                                                    <span>
                                                        {util.formatDate(d.startDate, {
                                                            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                                                        })}
                                                        {' '}{localTimeString(new Date(d.startDate))}–{localTimeString(new Date(d.endDate))}
                                                    </span>
                                                    <button type='button' class='btn btn-sm btn-link text-danger' onClick={() => this.removeDate(index)}>
                                                        <i class='fa-duotone fa-trash' />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>)}
                            </div>
                        </div>
                    </>)}

                    {error && (
                        <div class='alert alert-danger mx-3 rounded-lg'>{error}</div>
                    )}

                    <div class='w-100 d-flex flex-column justify-content-start px-4 py-3 bg-body fixed-bottom' style='bottom: 0; left: 0;'>
                        <button
                            type='button'
                            class='btn btn-primary rounded-pill'
                            onClick={this.create}
                            disabled={loading || !preset || count === 0}
                        >
                            {loading ? <i class='fa-duotone fa-spinner-third fa-spin me-2' /> : <i class='fa-duotone fa-plus me-2' />}
                            {count > 1 ? (
                                <Text id='convention.wizard-create-many' fields={{ count }}>Create {count} conventions</Text>
                            ) : (
                                <Text id='convention.wizard-create-one'>Create convention</Text>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ConventionWizard;
